import React, { useState, useEffect } from 'react';
import { MobileLine } from '../types/MobileLine';
import { showSuccess, showError } from '../utils/toastUtils';
import AdvancedSearchHelp from './AdvancedSearchHelp';

export interface FilterPreset {
  id: string;
  name: string;
  filters: FilterState;
  createdAt: string;
}

export interface FilterState {
  searchTerm: string;
  status: string[];
  valorMin: number | '';
  valorMax: number | '';
  dataMin: string;
  dataMax: string;
}

interface AdvancedFilterPanelProps {
  lines: MobileLine[];
  onFilterChange: (filteredLines: MobileLine[], filters: FilterState) => void;
  className?: string;
}

const AdvancedFilterPanel: React.FC<AdvancedFilterPanelProps> = ({
  lines,
  onFilterChange,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    status: [],
    valorMin: '',
    valorMax: '',
    dataMin: '',
    dataMax: '',
  });

  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Carregar presets do localStorage
  useEffect(() => {
    const saved = localStorage.getItem('filterPresets');
    if (saved) {
      try {
        setPresets(JSON.parse(saved));
      } catch (e) {
        console.error('Erro ao carregar presets:', e);
      }
    }
  }, []);

  // Aplicar filtros
  useEffect(() => {
    const filtered = lines.filter((line) => {
      // Search term
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const matchesSearch =
          line.numero?.toLowerCase().includes(term) ||
          line.nome?.toLowerCase().includes(term) ||
          line.cpfCnpj?.toLowerCase().includes(term) ||
          line.contrato?.toLowerCase().includes(term);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status.length > 0) {
        const lineStatus = line.status || 'semUsuario';
        if (!filters.status.includes(lineStatus)) return false;
      }

      // Valor range
      if (filters.valorMin !== '') {
        const valor = line.valorMensal || 0;
        if (valor < Number(filters.valorMin)) return false;
      }
      if (filters.valorMax !== '') {
        const valor = line.valorMensal || 0;
        if (valor > Number(filters.valorMax)) return false;
      }

      // Data range (data de bloqueio ou vínculo)
      if (filters.dataMin || filters.dataMax) {
        const dataVinculo = line.dataAdimplencia || line.dataInadimplencia || '';
        if (!dataVinculo) return false;

        const data = new Date(dataVinculo);
        if (filters.dataMin) {
          const dataMin = new Date(filters.dataMin);
          if (data < dataMin) return false;
        }
        if (filters.dataMax) {
          const dataMax = new Date(filters.dataMax);
          if (data > dataMax) return false;
        }
      }

      return true;
    });

    onFilterChange(filtered, filters);
  }, [filters, lines, onFilterChange]);

  const handleStatusToggle = (status: string) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      status: [],
      valorMin: '',
      valorMax: '',
      dataMin: '',
      dataMax: '',
    });
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      showError('Nome do preset não pode estar vazio');
      return;
    }

    const newPreset: FilterPreset = {
      id: Date.now().toString(),
      name: presetName,
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };

    const updated = [...presets, newPreset];
    setPresets(updated);
    localStorage.setItem('filterPresets', JSON.stringify(updated));
    showSuccess(`Preset "${presetName}" salvo!`);
    setPresetName('');
    setShowPresetDialog(false);
  };

  const handleLoadPreset = (preset: FilterPreset) => {
    setFilters(preset.filters);
    showSuccess(`Preset "${preset.name}" carregado!`);
  };

  const handleDeletePreset = (id: string) => {
    const updated = presets.filter((p) => p.id !== id);
    setPresets(updated);
    localStorage.setItem('filterPresets', JSON.stringify(updated));
    showSuccess('Preset removido!');
  };

  const activeFiltersCount = [
    filters.searchTerm,
    filters.status.length > 0,
    filters.valorMin !== '',
    filters.valorMax !== '',
    filters.dataMin,
    filters.dataMax,
  ].filter(Boolean).length;

  return (
    <div className={`bg-zinc-800 rounded-lg shadow-lg overflow-hidden ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex justify-between items-center hover:bg-zinc-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">🔍</span>
          <span className="font-bold text-white">Filtros Avançados</span>
          {activeFiltersCount > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowHelp(true);
            }}
            className="text-gray-400 hover:text-blue-400 transition-colors text-lg"
            title="Ver ajuda"
          >
            ❓
          </button>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-6 border-t border-zinc-700 space-y-6">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              🔎 Buscar por Número, Nome, CPF ou Contrato
            </label>
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, searchTerm: e.target.value }))
              }
              placeholder="Digite para buscar..."
              className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              📊 Status
            </label>
            <div className="flex flex-wrap gap-2">
              {['adimplente', 'inadimplente', 'semUsuario'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusToggle(status)}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    filters.status.includes(status)
                      ? status === 'adimplente'
                        ? 'bg-green-600 text-white'
                        : status === 'inadimplente'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-gray-600 text-white'
                      : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                  }`}
                >
                  {status === 'adimplente'
                    ? '✓ Adimplentes'
                    : status === 'inadimplente'
                      ? '⚠ Inadimplentes'
                      : '? Sem Usuário'}
                </button>
              ))}
            </div>
          </div>

          {/* Valor Range */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              💰 Valor Mensal (R$)
            </label>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="number"
                  value={filters.valorMin}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      valorMin: e.target.value === '' ? '' : Number(e.target.value),
                    }))
                  }
                  placeholder="Mínimo"
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <input
                  type="number"
                  value={filters.valorMax}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      valorMax: e.target.value === '' ? '' : Number(e.target.value),
                    }))
                  }
                  placeholder="Máximo"
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Data Range */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              📅 Período (Vínculo/Bloqueio)
            </label>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="date"
                  value={filters.dataMin}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, dataMin: e.target.value }))
                  }
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <input
                  type="date"
                  value={filters.dataMax}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, dataMax: e.target.value }))
                  }
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <button
              onClick={handleResetFilters}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              🔄 Limpar Filtros
            </button>
            <button
              onClick={() => setShowPresetDialog(true)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              💾 Salvar como Preset
            </button>
          </div>

          {/* Presets */}
          {presets.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                ⭐ Meus Presets
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between bg-zinc-700 p-3 rounded"
                  >
                    <button
                      onClick={() => handleLoadPreset(preset)}
                      className="flex-1 text-left hover:text-blue-400 transition-colors"
                    >
                      <span className="font-medium text-white">{preset.name}</span>
                      <span className="text-xs text-gray-400 block">
                        {new Date(preset.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </button>
                    <button
                      onClick={() => handleDeletePreset(preset.id)}
                      className="ml-2 text-red-400 hover:text-red-600 font-bold"
                      title="Deletar preset"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Preset Dialog */}
          {showPresetDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-zinc-800 p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
                <h3 className="text-xl font-bold text-white mb-4">Salvar Filtros como Preset</h3>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Nome do preset (ex: Clientes VIP, Inadimplentes há 30 dias)"
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded mb-4 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowPresetDialog(false)}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSavePreset}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Modal */}
      <AdvancedSearchHelp isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default AdvancedFilterPanel;
